import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { League } from "@/types/api.types";

interface Props {
  league?: League | null; // null = create mode
  onSave: (data: { name: string; isPrivate?: boolean; maxMembers?: number }) => void;
  onClose: () => void;
  isSaving: boolean;
}

export default function LeagueFormModal({ league, onSave, onClose, isSaving }: Props) {
  const isEdit = !!league;
  const [name, setName] = useState(league?.name || "");
  const [isPrivate, setIsPrivate] = useState(league?.isPrivate ?? true);
  const [maxMembers, setMaxMembers] = useState(league?.maxMembers ?? 20);
  const [errors, setErrors] = useState<{ name?: string; maxMembers?: string }>({});

  useEffect(() => {
    if (league) {
      setName(league.name);
      setIsPrivate(league.isPrivate);
      setMaxMembers(league.maxMembers);
    } else {
      setName("");
      setIsPrivate(true);
      setMaxMembers(20);
    }
    setErrors({});
  }, [league]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: typeof errors = {};
    if (!name.trim()) newErrors.name = "League name is required";
    if (maxMembers < 2) newErrors.maxMembers = "Must allow at least 2 members";
    if (Object.keys(newErrors).length) { setErrors(newErrors); return; }
    setErrors({});
    onSave({ name: name.trim(), isPrivate, maxMembers });
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
      <div className="fixed inset-0 flex items-center justify-center z-60 p-6">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <h3 className="text-lg font-bold text-gray-900">
              {isEdit ? "Edit League" : "Create League"}
            </h3>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                League Name <span className="text-red-500">*</span>
              </label>
              <Input
                value={name}
                onChange={(e) => { setName(e.target.value); if (e.target.value.trim()) setErrors((er) => ({ ...er, name: undefined })); }}
                placeholder="e.g. Champions League"
                maxLength={30}
                className={errors.name ? "border-red-400" : ""}
              />
              {errors.name && <p className="text-xs text-red-500 mt-1 font-medium">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Members
              </label>
              <Input
                type="number"
                value={maxMembers}
                onChange={(e) => { setMaxMembers(Number(e.target.value)); setErrors((er) => ({ ...er, maxMembers: undefined })); }}
                min={2}
                max={999999}
                className={errors.maxMembers ? "border-red-400" : ""}
              />
              {errors.maxMembers && <p className="text-xs text-red-500 mt-1 font-medium">{errors.maxMembers}</p>}
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="isPrivate"
                checked={isPrivate}
                onChange={(e) => setIsPrivate(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              <label htmlFor="isPrivate" className="text-sm font-medium text-gray-700">
                Private (requires join code)
              </label>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? "Saving..." : isEdit ? "Save Changes" : "Create League"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
